import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField,
  Button, InputAdornment, IconButton, Alert,
  CircularProgress, useMediaQuery, Dialog,
  DialogTitle, DialogContent, DialogContentText,
  DialogActions, Link
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Computer as ComputerIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

function Login() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    senha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot Password
  const [openForgot, setOpenForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.senha) {
      setError('Preencha todos os campos');
      setLoading(false);
      return;
    }

    try {
      const result = await signIn(formData.email, formData.senha);

      if (result.success) {
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard');
      } else {
        setError(result.error || 'Email ou senha inválidos');
      }
    } catch (error) {
      setError('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.warning('Digite seu email');
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetSuccess(true);
        toast.success('Email de recuperação enviado!');
        setTimeout(() => {
          setOpenForgot(false);
          setResetSuccess(false);
          setResetEmail('');
        }, 3000);
      } else {
        setResetError(data.error || 'Erro ao enviar email');
      }
    } catch (error) {
      console.error('Erro:', error);
      setResetError('Erro ao processar solicitação');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0A2647 0%, #1E3A5F 100%)',
        p: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            bgcolor: '#0A2647',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <ComputerIcon sx={{ fontSize: 40, color: '#39FF14' }} />
          <Typography variant="h4" sx={{ color: '#39FF14', fontWeight: 600 }}>
            JobsonInfo
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" align="center" sx={{ mb: 3, color: '#0A2647', fontWeight: 600 }}>
            Acessar Sistema
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#0A2647' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Senha"
              name="senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.senha}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#0A2647' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setOpenForgot(true)}
                sx={{
                  color: '#0A2647',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Esqueceu a senha?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                bgcolor: '#0A2647',
                '&:hover': {
                  bgcolor: '#1E3A5F',
                },
                '&:disabled': {
                  bgcolor: '#cccccc',
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Entrar'}
            </Button>
          </form>

          <Typography variant="body2" align="center" sx={{ color: '#666', mt: 2 }}>
            © {new Date().getFullYear()} JobsonInfo. Todos os direitos reservados.
          </Typography>
        </CardContent>
      </Card>

      {/* Dialog - Esqueci a senha */}
      <Dialog
        open={openForgot}
        onClose={() => !resetLoading && setOpenForgot(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#0A2647',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LockIcon sx={{ color: '#0A2647' }} />
            Recuperar Senha
          </Box>
          <IconButton onClick={() => setOpenForgot(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              ✅ Um link de recuperação foi enviado para seu email!
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Digite seu email cadastrado para receber um link de recuperação de senha.
              </DialogContentText>
              
              {resetError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {resetError}
                </Alert>
              )}
              
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="seu@email.com"
                disabled={resetLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
          <Button
            onClick={() => setOpenForgot(false)}
            disabled={resetLoading}
            fullWidth={isMobile}
            sx={{ color: '#666' }}
          >
            Cancelar
          </Button>
          {!resetSuccess && (
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              disabled={resetLoading || !resetEmail}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                bgcolor: '#0A2647',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1E3A5F',
                },
                '&:disabled': {
                  bgcolor: '#cccccc',
                  color: '#fff',
                }
              }}
            >
              {resetLoading ? <CircularProgress size={20} color="inherit" /> : 'Enviar Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
